import { Stack, Group, Badge, Grid, Text, Tooltip } from '@mantine/core';
import { TariffModalRow } from 'columns/ModalTariffSelect';

import {
  formatLocationModifierLabel,
  getLocationModifierTooltipLabel,
} from 'utils/tariffUtils';
import type { ModalTariffSelectTableProps } from './ModalTariffSelectTable';
import { formatCad, formatPercent } from './utils/tariffHelpers';
import { formatDateOnly } from 'utils/dateUtils';
import { DateTime } from 'luxon';

const formatEffectiveDate = (value?: string) => {
  return formatDateOnly(value, {
    fallback: value || 'N/A',
    format: DateTime.DATE_MED,
  });
};

export const TariffDetailPanel = ({
  row,
  locationServiceRate,
}: {
  row: TariffModalRow;
  locationServiceRate: ModalTariffSelectTableProps['locationServiceRate'];
}) => {
  const isModifier = row.type === 'Modifier';
  const shouldApplyLocationModifier = row.applyLocationModifier !== false;
  const locationModifierLabel =
    formatLocationModifierLabel(locationServiceRate);
  const detailItems = [
    { label: 'Description', value: row.description || 'No description' },
    { label: 'Type', value: row.type || 'N/A' },
    { label: 'Effective Date', value: formatEffectiveDate(row.effectiveDate) },
    { label: 'Specialty Code', value: row.specialtyCode || 'N/A' },
    {
      label: 'Percentage',
      value: `${row.percentage ?? 100}%`,
    },
    {
      label: 'Units',
      value: String(row.units ?? 1),
    },
    {
      label: 'Base Fee',
      value:
        row.type === 'Modifier' ? formatPercent(row.rate) : formatCad(row.rate),
    },
    {
      label: 'Submitted Amount',
      value: formatCad(row.submittedAmount),
    },
    ...(isModifier || !shouldApplyLocationModifier
      ? []
      : [
          {
            label: 'Location Modifier',
            value: locationModifierLabel,
          },
        ]),

    ...(row.debit
      ? [
          {
            label: 'Amount Paid',
            value: formatCad(row.debit.paidAmount ?? 0),
          },
          {
            label: 'Difference',
            value: formatCad(row.difference),
          },
        ]
      : []),
    {
      label: 'Original Rate',
      value:
        row.originalRate != null
          ? row.type === 'Modifier'
            ? formatPercent(row.originalRate)
            : formatCad(row.originalRate)
          : 'N/A',
    },
    {
      label: 'Manual Override',
      value:
        row.isManualTotal && row.total != null ? formatCad(row.total) : 'None',
    },
  ];

  return (
    <Stack
      gap="md"
      w="100%"
      maw="100%"
      style={{ flex: 1 }}
      p="md"
      data-testid="tariff-detail-panel"
    >
      <Group gap="xs">
        {row.isPendingResubmission && (
          <Badge color="yellow" variant="light">
            Pending resubmission
          </Badge>
        )}
        {row.isResubmitted && !row.isPendingResubmission && (
          <Badge color="yellow" variant="light">
            Resubmitted
          </Badge>
        )}
      </Group>
      <Grid gutter="md" w="100%" columns={3}>
        {detailItems.map((item) => (
          <Grid.Col span={{ base: 3, md: 1 }} key={item.label}>
            <Text size="xs" c="dimmed">
              {item.label}
            </Text>
            {item.label === 'Location Modifier' ? (
              <Tooltip
                label={getLocationModifierTooltipLabel(locationServiceRate)}
                withArrow
                position="top-start"
              >
                <Text size="sm" data-testid="location-differential-text">
                  {item.value}
                </Text>
              </Tooltip>
            ) : (
              <Text size="sm">{item.value}</Text>
            )}
          </Grid.Col>
        ))}
      </Grid>
    </Stack>
  );
};
